/**
 * Default token-provider factory for the Google calendar enrichment path.
 *
 * Calls the `googleCalendarMintAccessToken` Cloud Function (which uses the
 * server-stored refresh token to mint a short-lived access token) and
 * caches the result in memory until it's close to expiry. The browser then
 * uses the access token to talk to Google directly — Google's response
 * goes nowhere near our infrastructure.
 *
 * In-memory only: re-mounting / reloading the page issues a fresh mint.
 * No localStorage, no IndexedDB.
 *
 * @typedef {{ accessToken: string|null, expiresAt: number|null }} GoogleCredentials
 *
 * @param {{
 *   mint: () => Promise<{ accessToken: string|null, expiresAt: number|null }>,
 *   now?: () => number,
 *   refreshSkewMs?: number,
 * }} opts
 * @returns {() => Promise<GoogleCredentials | null>}
 */
export function createGoogleCalendarTokenProvider(opts) {
  const { mint, now = () => Date.now(), refreshSkewMs = 5 * 60 * 1000 } = opts;
  let cached = null;
  /** @type {Promise<GoogleCredentials|null>|null} */
  let inflight = null;

  function isStillUsable(creds) {
    if (!creds || !creds.accessToken) return false;
    if (!creds.expiresAt) return false;
    return creds.expiresAt - now() > refreshSkewMs;
  }

  return async function googleTokenProvider() {
    if (isStillUsable(cached)) return cached;
    if (inflight) return inflight;

    inflight = (async () => {
      try {
        const result = await mint();
        if (!result || !result.accessToken) {
          cached = null;
          return null;
        }
        cached = {
          accessToken: result.accessToken,
          expiresAt: typeof result.expiresAt === "number" ? result.expiresAt : null,
        };
        return cached;
      } catch {
        // Token-mint failures (calendar not linked, refresh token expired, etc.)
        // surface as null credentials — the cache layer + provider then map
        // that to permission_denied EnrichmentResult entries, which the UI
        // turns into a one-time "re-link calendar" toast.
        cached = null;
        return null;
      } finally {
        inflight = null;
      }
    })();

    return inflight;
  };
}

/**
 * Wire the default token provider to the Cloud Function via Firebase's
 * httpsCallable. Mounted at the app root so the EnrichmentCacheProvider's
 * tokenProviders prop carries this for the 'google' key.
 *
 * @param {{ httpsCallable: (name: string) => () => Promise<{ data: { accessToken, expiresAt, scope } }> }} firebase
 * @returns {() => Promise<GoogleCredentials | null>}
 */
export function defaultGoogleCalendarTokenProvider({ httpsCallable }) {
  const callable = httpsCallable("googleCalendarMintAccessToken");
  return createGoogleCalendarTokenProvider({
    mint: async () => {
      const response = await callable();
      const data = response?.data || {};
      return {
        accessToken: data.accessToken || null,
        expiresAt: typeof data.expiresAt === "number" ? data.expiresAt : null,
      };
    },
  });
}
